import * as SBService from "helpers/sportbookService";
import { getAssetFullUrl } from "helpers/utils";
import moment from "moment";
import { useRef, useState } from "react";
import Resizer from "react-image-file-resizer";
import toast from "react-hot-toast";

/**
 * Ô "Đóng phí" của người chơi: mã QR chuyển khoản, nội dung chuyển khoản, mức
 * phí và hạn đóng phí của giải.
 *
 * Dùng chung cho cả màn giải thường (LeaguePreJoin) và giải Swiss
 * (swiss/live) - cả hai đều bọc trong `.biadi` nên lớp .modalwrap/.modal của
 * shared/league.css áp được ở cả hai nơi.
 *
 * Giải cấu hình theo lối cũ (thu tiền mặt tại địa điểm, không có QR lẫn nội
 * dung CK) vẫn mở được ô này: khi đó nó hiện đúng câu "đóng tại địa điểm khi
 * check-in" như trước chứ không bày ra một khung trống.
 *
 * Nộp lại được: backend ghi đè đúng một file cho mỗi (giải, cơ thủ), nên người
 * chơi gửi sai ảnh thì chỉ cần chọn ảnh khác.
 */

/**
 * Nén ảnh biên lai rồi trả base64 THUẦN (đã bỏ tiền tố data:) - khuôn giống
 * ảnh đại diện lúc đăng ký.
 *
 * Xuất PNG chứ không JPEG: backend lưu file tên `.png` và GCS suy ra
 * Content-Type từ đuôi file, nên gửi byte JPEG sẽ thành ảnh png chứa jpeg.
 * Biên lai phần lớn là ảnh chụp màn hình - PNG vừa đúng đuôi vừa nét.
 */
const anhBase64ThuanTuFile = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    try {
      Resizer.imageFileResizer(
        file,
        1000,
        1000,
        "PNG",
        100,
        0,
        (uri: any) => {
          const s = String(uri);
          resolve(s.substr(s.indexOf(",") + 1));
        },
        "base64",
      );
    } catch (err) {
      reject(err);
    }
  });

const money = (v: any) => {
  const n = Number(String(v).replace(/\D/g, ""));
  return isNaN(n) ? v : n.toLocaleString("vi-VN");
};

/**
 * Nội dung chuyển khoản do ops soạn có thể chứa {SDT} - chỗ dành cho số điện
 * thoại của người đang đăng nhập, vì ngân hàng chỉ đối chiếu được người nộp
 * qua nội dung CK. Chưa biết SĐT thì giữ nguyên chỗ trống: thay bằng chuỗi
 * rỗng sẽ tạo ra nội dung CK thiếu, mà người chơi lại không nhận ra là thiếu.
 */
export const fillTransferNote = (note: string, phone?: string | null) => {
  if (!note) return "";
  if (!phone) return note;
  return note.replace(/\{\s*SDT\s*\}/gi, phone);
};

export default function FeePaymentModal({
  league,
  feeDeadline,
  phone,
  billUploaded,
  billImage,
  onClose,
}: {
  league: any;
  feeDeadline?: string | Date | null;
  phone?: string | null;
  billUploaded?: boolean;
  /** Đường dẫn ảnh biên lai đã nộp, nếu có. Hiện lại ngay trong ô này. */
  billImage?: string | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [dangTai, setDangTai] = useState(false);
  /* Nộp xong thì hiện ngay trạng thái mới tại chỗ: /me chỉ được đọc lúc mở
     trang nên chờ nó refetch sẽ ra "chưa nộp" dù vừa nộp thành công. */
  const [daNop, setDaNop] = useState(!!billUploaded);
  /* Ảnh đang hiện: ảnh đã nộp từ trước, hoặc ảnh vừa nộp xong trong lần mở này.
     Đây là BẰNG CHỨNG của một thao tác không có nút Lưu - chọn ảnh là nộp luôn, nên
     người dùng phải nhìn thấy đúng cái mình vừa gửi đi, không phải một dòng chữ. */
  const [anhBienLai, setAnhBienLai] = useState<string | null>(billImage ?? null);
  const inputRef = useRef<HTMLInputElement>(null);

  const qr = getAssetFullUrl(league?.feeQrImage);
  const note = fillTransferNote(league?.feeTransferNote || "", phone);
  const deadline = feeDeadline ?? league?.feeDeadlineDate ?? null;
  const deadlineText = deadline && moment(deadline).isValid() ? moment(deadline).format("DD/MM/YYYY") : null;

  /* Không có QR lẫn nội dung CK thì giải này vẫn thu tiền mặt - đừng hứa
     chuyển khoản. */
  const hasTransferInfo = !!qr || !!note;

  const copyNote = async () => {
    try {
      await navigator.clipboard.writeText(note);
      setCopied(true);
      toast.success("Đã copy nội dung chuyển khoản");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Không copy được, bạn chép tay giúp nhé");
    }
  };

  const chonAnh = async (e: any) => {
    const file = e.target?.files?.[0];
    // Cho chọn lại đúng file vừa chọn: input file không bắn onChange nếu value
    // không đổi, nên nộp lại cùng một ảnh sẽ im lặng không làm gì.
    if (e.target) e.target.value = "";
    if (!file) return;
    if (!String(file.type || "").startsWith("image/")) {
      toast.error("Chỉ nhận tệp ảnh");
      return;
    }
    try {
      setDangTai(true);
      const base64 = await anhBase64ThuanTuFile(file);
      const rsp: any = await SBService.uploadFeeBill(league.id, base64);
      const data = rsp?.data ?? rsp;
      setDaNop(true);
      /* Backend trả về đường dẫn ảnh vừa lưu. Thiếu nó thì rơi về xem trước tại chỗ
         bằng chính tệp người dùng chọn - vẫn đúng ảnh đó, và vẫn hơn không có gì. */
      setAnhBienLai(data?.billImage || URL.createObjectURL(file));
      toast.success("Đã nộp ảnh biên lai. Chờ ban tổ chức xác nhận.");
    } catch (error: any) {
      const err = error?.response?.data?.error;
      const msg: Record<string, string> = {
        leagueHasNoFee: "Giải này không thu phí.",
        didNotJoinLeague: "Bạn chưa đăng ký giải này.",
        leagueNotFound: "Không tìm thấy giải đấu.",
      };
      toast.error(msg[err] || "Không nộp được ảnh biên lai, thử lại giúp nhé");
    } finally {
      setDangTai(false);
    }
  };

  return (
    <div className="modalwrap">
      <div className="modalbd" onClick={onClose} />
      <div className="modal">
        <div className="label" style={{ marginBottom: 10 }}>
          Đóng phí tham gia
        </div>

        <div className="callout">
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{league?.name}</div>
          <div className="small">
            Lệ phí <b style={{ color: "var(--chalk)" }}>{money(league?.fee)} đ</b>
            {deadlineText ? (
              <>
                {" · "}hạn đóng <b style={{ color: "var(--chalk)" }}>{deadlineText}</b>
              </>
            ) : null}
          </div>
        </div>

        {hasTransferInfo ? (
          <>
            {qr ? (
              <div style={{ marginTop: 14 }}>
                <div className="tiny" style={{ marginBottom: 6 }}>
                  Quét mã để chuyển khoản
                </div>
                {/* Nền trắng + bo góc: mã QR trên panel tối sẽ mất tương phản và
                    máy không đọc được. maxWidth giữ ảnh không tràn ngang ở 390px. */}
                <img
                  alt="Mã QR chuyển khoản lệ phí"
                  src={qr}
                  style={{
                    display: "block",
                    width: "100%",
                    maxWidth: 220,
                    height: "auto",
                    margin: "0 auto",
                    background: "#fff",
                    padding: 8,
                    borderRadius: 8,
                  }}
                />
              </div>
            ) : null}

            {note ? (
              <div style={{ marginTop: 14 }}>
                <div className="tiny" style={{ marginBottom: 6 }}>
                  Nội dung chuyển khoản
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    border: "1px solid var(--line)",
                    borderRadius: 8,
                    padding: "8px 10px",
                  }}
                >
                  {/* wordBreak: nội dung CK dài không được đẩy ngang cái modal. */}
                  <code style={{ flex: 1, minWidth: 0, wordBreak: "break-word", fontSize: 13 }}>{note}</code>
                  <button className="btn btn-ghost" style={{ flexShrink: 0 }} onClick={copyNote}>
                    {copied ? "Đã copy" : "Copy"}
                  </button>
                </div>
                <div className="tiny" style={{ marginTop: 6 }}>
                  Ghi đúng nội dung này để ban tổ chức đối chiếu được.
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="small" style={{ marginTop: 14 }}>
            Lệ phí đóng tại địa điểm khi check-in.
          </div>
        )}

        {/* Chỉ giải có đường chuyển khoản mới có "biên lai" để nộp; giải thu
            tiền mặt tại địa điểm thì mời nộp ảnh là vô nghĩa. */}
        {hasTransferInfo ? (
          <div style={{ marginTop: 14 }}>
            <div className="tiny" style={{ marginBottom: 6 }}>
              Ảnh biên lai chuyển khoản
            </div>
            {daNop ? (
              <div style={{ marginBottom: 8 }}>
                <div className="small" style={{ color: "var(--chalk)" }}>
                  ✓ Đã nộp ảnh biên lai — đang chờ ban tổ chức xác nhận.
                </div>
                {anhBienLai ? (
                  <img
                    alt="Ảnh biên lai đã nộp"
                    {...{
                      /* blob: là ảnh xem trước tại máy (đường dự phòng khi backend
                         không trả đường dẫn), KHÔNG được ghép tiền tố asset vào. */
                      src: anhBienLai.startsWith("blob:")
                        ? anhBienLai
                        : getAssetFullUrl(anhBienLai) || anhBienLai,
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      maxWidth: 180,
                      /* CHẶN CHIỀU CAO. Biên lai hay là ảnh chụp màn hình dọc, tỉ lệ
                         9:19 - để "auto" thì một ảnh rộng 180px cao gần 400px, đẩy
                         nút Đóng xuống dưới đáy máy. Đây là ảnh xem lại cho biết "đã
                         nộp cái gì", không phải chỗ đọc chi tiết biên lai. */
                      maxHeight: 200,
                      objectFit: "cover",
                      objectPosition: "top",
                      marginTop: 8,
                      borderRadius: 8,
                      border: "1px solid var(--line)",
                    }}
                  />
                ) : null}
              </div>
            ) : null}
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={chonAnh}
            />
            <button
              className="btn btn-ghost btn-block"
              disabled={dangTai}
              onClick={() => inputRef.current?.click()}
            >
              {dangTai ? "Đang tải ảnh…" : daNop ? "Nộp lại ảnh khác" : "Chọn ảnh biên lai"}
            </button>
          </div>
        ) : null}

        <div className="small" style={{ marginTop: 14 }}>
          Đăng ký chỉ chính thức sau khi ban tổ chức xác nhận đã nhận phí.
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
}
